import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { depositKeys } from '@/lib/queryKeys';
import { billingV2Endpoints } from '@/lib/api/endpoints';
import { billingV2Post } from '@/lib/api/clients/billing-v2';
import type { RefundDepositInput, RefundDepositResponse } from '@/types/deposit';

interface RefundArgs extends RefundDepositInput {
  depositId: string;
}

/**
 * Spec §4.4 — refund a held (or partially-applied) deposit. Server-side
 * blocks refund-of-applied with 422 DEPOSIT_ALREADY_APPLIED; the toast
 * message surfaces the structured error code.
 */
export function useRefundDeposit() {
  const queryClient = useQueryClient();

  return useApiMutation<RefundDepositResponse, RefundArgs>({
    mutationFn: ({ depositId, ...body }) =>
      billingV2Post<RefundDepositResponse, RefundDepositInput>(
        billingV2Endpoints.depositRefund(depositId),
        body,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(depositKeys.detail(data.deposit.id), data.deposit);
      void queryClient.invalidateQueries({ queryKey: depositKeys.all() });
      toast.success('Deposit refunded');
    },
  });
}
